import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  FormControlLabel,
  Switch,
  InputAdornment,
  Collapse,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FiberManualRecord as BulletIcon,
  AccountTree as TreeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
  Link as LinkIcon,
  AddLink as AddLinkIcon,
  LinkOff as LinkOffIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import skillService, { SkillSearchResult, SkillFlat } from '../../services/skill.service';
import skillContainerService from '../../services/skillContainerService';
import useSkillStore from '../../store/skillStore';
import patternContainerService from '../../services/patternContainerService';
import PatternMindMap, { PatternMindMapRef } from '../../components/PatternMindMap';
import { ExercisePattern, PatternContainer } from '../../types';
import api from '../../services/api';
import SkillCriteriaSection from '../../components/SkillCriteriaSection';

interface Container {
  id: number;
  title: string;
  parent: number | null;
  children?: Container[];
  skills?: Skill[];
}

interface ChildSkill {
  id: number;
  name: string;
  is_reference: boolean;
  skill_link_id: number | null;
  reference_patterns_details: Array<{id: number; title: string; container: number | null; container_title: string | null}>;
  children_skills: ChildSkill[];
}

interface Skill {
  id: number;
  name: string;
  description: string;
  container: number | null;
  reference_patterns_details?: any[];
  is_public?: boolean;
  parent?: number | null;
  children_skills?: ChildSkill[];
}

interface SkillsProps {
  onNavigateToPattern?: (patternId: number, containerId: number | null) => void;
}

const Skills: React.FC<SkillsProps> = ({ onNavigateToPattern }) => {
  const [rootContainers, setRootContainers] = useState<Container[]>([]);
  const [loading, setLoading] = useState(false);
  const [collapsedContainers, setCollapsedContainers] = useState<Set<number>>(new Set());
  const [expandedSkills, setExpandedSkills] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  // Pattern tree modal state
  const [patternTreeOpen, setPatternTreeOpen] = useState(false);
  const [allPatternsForTree, setAllPatternsForTree] = useState<ExercisePattern[]>([]);
  const [allContainersForTree, setAllContainersForTree] = useState<PatternContainer[]>([]);
  const [treeSearchQuery, setTreeSearchQuery] = useState('');
  const [isCreatingSkill, setIsCreatingSkill] = useState(false); // Track if tree is for creating or editing
  const [isExpandModeActive, setIsExpandModeActive] = useState(false);
  const [isCollapseSiblingsModeActive, setIsCollapseSiblingsModeActive] = useState(false);
  const mindMapRef = useRef<PatternMindMapRef>(null);

  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createType, setCreateType] = useState<'root' | 'subcontainer' | 'skill' | 'child_skill'>('root');
  const [selectedParent, setSelectedParent] = useState<number | null>(null);
  const [parentSkillForChild, setParentSkillForChild] = useState<{id: number; name: string; container: number | null} | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [newPatterns, setNewPatterns] = useState<any[]>([]);

  // Edit dialogs
  const [editContainerOpen, setEditContainerOpen] = useState(false);
  const [editingContainer, setEditingContainer] = useState<Container | null>(null);
  const [editContainerTitle, setEditContainerTitle] = useState('');

  const [editSkillOpen, setEditSkillOpen] = useState(false);
  const [editingSkill, setEditingSkill] = useState<Skill | null>(null);
  const [editSkillName, setEditSkillName] = useState('');
  const [editSkillDescription, setEditSkillDescription] = useState('');
  const [editSkillPatterns, setEditSkillPatterns] = useState<any[]>([]);
  const [editSkillIsPublic, setEditSkillIsPublic] = useState(false);

  // Skill detail modal
  const [skillDetailOpen, setSkillDetailOpen] = useState(false);
  const [detailSkill, setDetailSkill] = useState<Skill | null>(null);

  // Reference picker
  const [refPickerOpen, setRefPickerOpen] = useState(false);
  const [refPickerParentSkillId, setRefPickerParentSkillId] = useState<number | null>(null);
  const [refSearchQuery, setRefSearchQuery] = useState('');
  const [refSearchResults, setRefSearchResults] = useState<SkillSearchResult[]>([]);
  const [refSearchLoading, setRefSearchLoading] = useState(false);
  const refSearchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Skill store for level-by-level loading with cache
  const skillStore = useSkillStore();
  const cacheVersion = useSkillStore(state => state.cacheVersion);

  // Convert a flat skill from store to the Skill interface used by rendering
  // Uses useSkillStore.getState() to always read the latest store state
  // ancestors tracks the current recursion path to prevent infinite loops
  const flatToSkill = (flat: SkillFlat): Skill => {
    const store = useSkillStore.getState();
    const children = store.getChildren(flat.id);
    const hasChildren = flat.children_ids.length > 0 || flat.child_link_ids.length > 0;
    const childrenLoaded = store.loadedChildren.has(flat.id);
    const ancestors = new Set<number>([flat.id]);

    return {
      id: flat.id,
      name: flat.name,
      description: flat.description,
      container: flat.container,
      reference_patterns_details: flat.reference_patterns_details,
      is_public: flat.is_public,
      parent: flat.parent,
      children_skills: childrenLoaded ? children.map(c => ({
        id: c.id,
        name: c.name,
        is_reference: c.is_reference,
        skill_link_id: c.skill_link_id,
        reference_patterns_details: c.reference_patterns_details,
        children_skills: ancestors.has(c.id) ? [] : flatToChildSkills(c.id, ancestors),
      })) : (hasChildren ? [{ id: -1, name: '...', is_reference: false, skill_link_id: null, reference_patterns_details: [], children_skills: [] }] : []),
    };
  };

  const flatToChildSkills = (skillId: number, parentAncestors: Set<number>): ChildSkill[] => {
    const store = useSkillStore.getState();
    if (!store.loadedChildren.has(skillId)) return [];
    // Create a new ancestors set for this branch (path from root to here)
    const ancestors = new Set(parentAncestors);
    ancestors.add(skillId);
    const children = store.getChildren(skillId);
    return children.map(c => ({
      id: c.id,
      name: c.name,
      is_reference: c.is_reference,
      skill_link_id: c.skill_link_id,
      reference_patterns_details: c.reference_patterns_details,
      children_skills: ancestors.has(c.id) ? [] : flatToChildSkills(c.id, ancestors),
    }));
  };

  // Load all data in hierarchical structure
  const initialLoadDone = useRef(false);

  const loadAllData = async () => {
    try {
      if (!initialLoadDone.current) {
        setLoading(true);
      }

      // Load root containers (parent = null)
      const rootCont = await skillContainerService.getContainers(null);

      // For each root container, load subcontainers and root skills (flat)
      const enrichedRoots = await Promise.all(
        rootCont.map(async (root: Container) => {
          // Load subcontainers
          const subcontainers = await skillContainerService.getContainers(root.id);

          // For each subcontainer, load its root skills via store (flat, cached)
          const enrichedSubs = await Promise.all(
            subcontainers.map(async (sub: Container) => {
              const flatSkills = await skillStore.loadRoots(sub.id);
              const skills = flatSkills.map(f => flatToSkill(f));
              return { ...sub, skills };
            })
          );

          // Load skills directly in root container via store
          const flatRootSkills = await skillStore.loadRoots(root.id);
          const rootSkills = flatRootSkills.map(f => flatToSkill(f));

          return {
            ...root,
            children: enrichedSubs,
            skills: rootSkills
          };
        })
      );

      setRootContainers(enrichedRoots);

      // Fire-and-forget: BFS-load all descendants in background
      const allContainerIds: number[] = [];
      for (const root of enrichedRoots) {
        allContainerIds.push(root.id);
        for (const sub of (root.children || [])) {
          allContainerIds.push(sub.id);
        }
      }
      skillStore.loadAllDescendants(allContainerIds).catch(e =>
        console.error('BFS loading error:', e)
      );
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
    }
  };

  // Rebuild containers from store cache (no API call)
  const rebuildFromCache = () => {
    const store = useSkillStore.getState();
    const allSkills = Object.values(store.skills);
    setRootContainers(prev => prev.map(root => ({
      ...root,
      children: (root.children || []).map(sub => ({
        ...sub,
        skills: allSkills
          .filter(s => s.container === sub.id && s.parent === null)
          .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name))
          .map(f => flatToSkill(f)),
      })),
      skills: allSkills
        .filter(s => s.container === root.id && s.parent === null)
        .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name))
        .map(f => flatToSkill(f)),
    })));
  };

  /** Clear cache and reload all data */
  const reloadAll = () => {
    skillStore.clearCache();
    loadAllData();
  };

  useEffect(() => {
    loadAllData();
  }, []);

  // Auto-rebuild tree when BFS loads new levels
  useEffect(() => {
    if (initialLoadDone.current) {
      rebuildFromCache();
    }
  }, [cacheVersion]);

  const handleCreateRootContainer = async () => {
    if (!newTitle.trim()) {
      toast.error('Le nom est requis');
      return;
    }

    try {
      await skillContainerService.createContainer({
        title: newTitle,
        parent: null,
      });
      toast.success('Container créé');
      setCreateDialogOpen(false);
      setNewTitle('');
      reloadAll();
    } catch (error) {
      console.error('Error creating container:', error);
      toast.error('Erreur lors de la création');
    }
  };

  const handleCreateSubcontainer = async () => {
    if (!newTitle.trim() || !selectedParent) {
      toast.error('Le nom et le parent sont requis');
      return;
    }

    try {
      await skillContainerService.createContainer({
        title: newTitle,
        parent: selectedParent,
      });
      toast.success('Sous-container créé');
      setCreateDialogOpen(false);
      setNewTitle('');
      setSelectedParent(null);
      reloadAll();
    } catch (error) {
      console.error('Error creating subcontainer:', error);
      toast.error('Erreur lors de la création');
    }
  };

  const handleCreateSkill = async () => {
    if (!newTitle.trim() || !selectedParent) {
      toast.error('Le nom et le parent sont requis');
      return;
    }

    try {
      await skillService.create({
        name: newTitle,
        description: '',
        container: selectedParent,
        tags: [],
        reference_patterns: newPatterns.map(p => p.id),
      });
      toast.success('Savoir-faire créé');
      setCreateDialogOpen(false);
      setNewTitle('');
      setSelectedParent(null);
      setNewPatterns([]);
      reloadAll();
    } catch (error) {
      console.error('Error creating skill:', error);
      toast.error('Erreur lors de la création');
    }
  };

  const handleCreateChildSkill = async () => {
    if (!newTitle.trim() || !parentSkillForChild) {
      toast.error('Le nom est requis');
      return;
    }

    try {
      await skillService.create({
        name: newTitle,
        description: '',
        container: parentSkillForChild.container,
        parent: parentSkillForChild.id,
        tags: [],
        reference_patterns: newPatterns.map(p => p.id),
      });
      toast.success('Sous-savoir-faire créé');
      setCreateDialogOpen(false);
      setNewTitle('');
      setParentSkillForChild(null);
      setNewPatterns([]);
      reloadAll();
    } catch (error) {
      console.error('Error creating child skill:', error);
      toast.error('Erreur lors de la création');
    }
  };

  const handleOpenCreateChildSkill = (skill: {id: number; name: string; container: number | null}) => {
    setCreateType('child_skill');
    setParentSkillForChild(skill);
    setNewTitle('');
    setNewPatterns([]);
    setCreateDialogOpen(true);
  };

  const handleEditContainer = (container: Container) => {
    setEditingContainer(container);
    setEditContainerTitle(container.title);
    setEditContainerOpen(true);
  };

  const handleUpdateContainer = async () => {
    if (!editingContainer || !editContainerTitle.trim()) {
      toast.error('Le nom est requis');
      return;
    }

    try {
      await skillContainerService.updateContainer(editingContainer.id, {
        title: editContainerTitle,
      });
      toast.success('Container mis à jour');
      setEditContainerOpen(false);
      setEditingContainer(null);
      setEditContainerTitle('');
      reloadAll();
    } catch (error) {
      console.error('Error updating container:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteContainer = async (containerId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce container ?')) {
      return;
    }

    try {
      await skillContainerService.deleteContainer(containerId);
      toast.success('Container supprimé');
      reloadAll();
    } catch (error) {
      console.error('Error deleting container:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEditSkill = (skill: Skill) => {
    setEditingSkill(skill);
    setEditSkillName(skill.name);
    setEditSkillDescription(skill.description || '');
    setEditSkillPatterns(skill.reference_patterns_details || []);
    setEditSkillIsPublic(skill.is_public || false);
    setEditSkillOpen(true);
  };

  const handleUpdateSkill = async () => {
    if (!editingSkill || !editSkillName.trim()) {
      toast.error('Le nom est requis');
      return;
    }

    try {
      await skillService.update(editingSkill.id, {
        name: editSkillName,
        description: editSkillDescription,
        reference_patterns: editSkillPatterns.map(p => p.id),
        is_public: editSkillIsPublic,
      });
      toast.success('Savoir-faire mis à jour');
      setEditSkillOpen(false);
      setEditingSkill(null);
      setEditSkillName('');
      setEditSkillDescription('');
      setEditSkillPatterns([]);
      setEditSkillIsPublic(false);
      reloadAll();
    } catch (error) {
      console.error('Error updating skill:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteSkill = async (skillId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce savoir-faire ?')) {
      return;
    }

    try {
      await skillService.delete(skillId);
      toast.success('Savoir-faire supprimé');
      reloadAll();
    } catch (error) {
      console.error('Error deleting skill:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSkillClick = (skill: Skill) => {
    setDetailSkill(skill);
    setSkillDetailOpen(true);
  };

  const handlePatternNavigation = (pattern: any) => {
    setSkillDetailOpen(false);
    onNavigateToPattern?.(pattern.id, pattern.container ?? null);
  };

  const handleOpenPatternTree = async (forCreation: boolean = false) => {
    try {
      setLoading(true);
      setIsCreatingSkill(forCreation);

      // Load all patterns with pagination handling
      const fetchAllPatterns = async (): Promise<ExercisePattern[]> => {
        let allPatterns: ExercisePattern[] = [];
        let page = 1;
        let hasMore = true;

        while (hasMore) {
          const response = await api.get(`/exercise-patterns/?page=${page}&page_size=100`);
          const data = response.data;

          if (Array.isArray(data)) {
            allPatterns = allPatterns.concat(data);
            hasMore = false;
          } else if (data.results) {
            allPatterns = allPatterns.concat(data.results);
            hasMore = !!data.next;
            page++;
          } else {
            hasMore = false;
          }
        }

        return allPatterns;
      };

      const allPatterns = await fetchAllPatterns();
      setAllPatternsForTree(allPatterns);

      // Fetch all containers with a single request
      const allContainers = await patternContainerService.getAllContainers();
      setAllContainersForTree(allContainers);

      setPatternTreeOpen(true);
    } catch (error) {
      console.error('Error loading tree data:', error);
      toast.error('Erreur lors du chargement de l\'arborescence');
    } finally {
      setLoading(false);
    }
  };

  const handlePatternTreeSelect = (pattern: ExercisePattern) => {
    if (isCreatingSkill) {
      // Add to new patterns for creation
      if (!newPatterns.find(p => p.id === pattern.id)) {
        setNewPatterns([...newPatterns, pattern]);
      }
    } else {
      // Add to edit patterns
      if (!editSkillPatterns.find(p => p.id === pattern.id)) {
        setEditSkillPatterns([...editSkillPatterns, pattern]);
      }
    }
    setPatternTreeOpen(false);
  };

  // Reference management
  const handleOpenRefPicker = (parentSkillId: number) => {
    setRefPickerParentSkillId(parentSkillId);
    setRefSearchQuery('');
    setRefSearchResults([]);
    setRefPickerOpen(true);
  };

  const handleRefSearch = (query: string) => {
    setRefSearchQuery(query);
    if (refSearchTimeout.current) clearTimeout(refSearchTimeout.current);
    if (query.trim().length < 2) {
      setRefSearchResults([]);
      return;
    }
    refSearchTimeout.current = setTimeout(async () => {
      try {
        setRefSearchLoading(true);
        const results = await skillService.searchSkills(query);
        setRefSearchResults(results.filter(r => r.id !== refPickerParentSkillId));
      } catch (error) {
        console.error('Error searching skills:', error);
      } finally {
        setRefSearchLoading(false);
      }
    }, 300);
  };

  const handleAddReference = async (childSkillId: number) => {
    if (!refPickerParentSkillId) return;
    try {
      await skillService.addReference(refPickerParentSkillId, childSkillId);
      toast.success('Référence ajoutée');
      setRefPickerOpen(false);
      reloadAll();
    } catch (error: any) {
      const msg = error.response?.data?.error || 'Erreur lors de l\'ajout de la référence';
      toast.error(msg);
    }
  };

  const handleRemoveReference = async (parentSkillId: number, childSkillId: number, skillLinkId?: number) => {
    try {
      await skillService.removeReference(parentSkillId, childSkillId, skillLinkId);
      toast.success('Référence supprimée');
      reloadAll();
    } catch (error) {
      console.error('Error removing reference:', error);
      toast.error('Erreur lors de la suppression de la référence');
    }
  };

  const toggleSkill = async (skillId: number) => {
    const isExpanded = expandedSkills.has(skillId);
    if (isExpanded) {
      setExpandedSkills(prev => {
        const next = new Set(prev);
        next.delete(skillId);
        return next;
      });
    } else {
      // If children not yet cached (BFS hasn't reached this level), fetch on demand
      if (!skillStore.loadedChildren.has(skillId)) {
        try {
          await skillStore.expandSkill(skillId);
          // Bump cacheVersion to trigger rebuild via the effect
          useSkillStore.setState(prev => ({ cacheVersion: prev.cacheVersion + 1 }));
        } catch (e) {
          console.error('Error loading children:', e);
          toast.error('Erreur lors du chargement des sous-savoir-faire');
          return;
        }
      }
      setExpandedSkills(prev => {
        const next = new Set(prev);
        next.add(skillId);
        return next;
      });
    }
  };

  const collectChildPatterns = (children: ChildSkill[]): Array<{skillName: string; patterns: any[]}> => {
    const result: Array<{skillName: string; patterns: any[]}> = [];
    for (const child of children) {
      if (child.reference_patterns_details.length > 0) {
        result.push({ skillName: child.name, patterns: child.reference_patterns_details });
      }
      result.push(...collectChildPatterns(child.children_skills));
    }
    return result;
  };

  const renderChildSkills = (children: ChildSkill[], depth: number = 1, parentSkillId?: number, parentContainer?: number | null) => {
    return children.map((child, idx) => {
      const hasChildren = child.children_skills && child.children_skills.length > 0;
      const isExpanded = expandedSkills.has(child.id);
      const isRef = child.is_reference;

      return (
        <React.Fragment key={`${child.id}-${isRef ? 'ref' : 'child'}-${idx}`}>
          <ListItem
            sx={{
              py: 0.5,
              px: 1,
              pl: depth * 4,
              cursor: 'pointer',
              border: isRef
                ? '1px dashed rgba(156, 39, 176, 0.5)'
                : child.reference_patterns_details && child.reference_patterns_details.length > 0
                  ? '2px solid rgba(33, 150, 243, 0.6)'
                  : 'none',
              borderRadius: (isRef || (child.reference_patterns_details && child.reference_patterns_details.length > 0))
                ? 2
                : 0,
              backgroundColor: isRef ? 'rgba(156, 39, 176, 0.04)' : 'inherit',
              mb: 0.5,
              '&:hover': { backgroundColor: isRef ? 'rgba(156, 39, 176, 0.08)' : 'action.hover' }
            }}
            onClick={() => {
              handleSkillClick({
                id: child.id,
                name: child.name,
                description: '',
                container: null,
                reference_patterns_details: child.reference_patterns_details,
                children_skills: child.children_skills,
              });
            }}
          >
            {hasChildren && (
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); toggleSkill(child.id); }}
                sx={{ mr: 0.5, p: 0.25 }}
              >
                {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            )}
            {!hasChildren && (isRef
              ? <LinkIcon sx={{ fontSize: 14, mr: 1, color: 'secondary.main' }} />
              : <BulletIcon sx={{ fontSize: 8, mr: 1 }} />
            )}
            {isRef && (
              <Tooltip title="Référence vers un autre savoir-faire">
                <Chip label="ref" size="small" color="secondary" variant="outlined" sx={{ mr: 1, height: 20, fontSize: '0.65rem' }} />
              </Tooltip>
            )}
            <ListItemText
              primary={child.name}
              primaryTypographyProps={{ variant: 'body2', fontStyle: isRef ? 'italic' : 'normal' }}
              sx={{ my: 0 }}
            />
            {!isRef && (
              <>
                <Tooltip title="Ajouter un sous-savoir-faire">
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenCreateChildSkill({ id: child.id, name: child.name, container: parentContainer ?? null }); }} sx={{ color: 'primary.main' }}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Ajouter une référence">
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenRefPicker(child.id); }} sx={{ color: 'secondary.main' }}>
                    <AddLinkIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditSkill({ id: child.id, name: child.name, description: '', container: null, reference_patterns_details: child.reference_patterns_details, children_skills: child.children_skills }); }}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); handleDeleteSkill(child.id); }}
                  sx={{ color: 'error.main' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </>
            )}
            {isRef && parentSkillId && (
              <Tooltip title="Supprimer la référence">
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); handleRemoveReference(parentSkillId, child.id, child.skill_link_id || undefined); }}
                  sx={{ color: 'error.main' }}
                >
                  <LinkOffIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </ListItem>
          {hasChildren && isExpanded && (
            <Collapse in={isExpanded}>
              {renderChildSkills(child.children_skills, depth + 1, child.id, parentContainer)}
            </Collapse>
          )}
        </React.Fragment>
      );
    });
  };

  const renderSkillItem = (skill: Skill) => {
    const hasChildren = skill.children_skills && skill.children_skills.length > 0;
    const isExpanded = expandedSkills.has(skill.id);

    return (
      <React.Fragment key={skill.id}>
        <ListItem
          sx={{
            py: 0.5,
            px: 1,
            cursor: 'pointer',
            backgroundColor: skill.is_public ? 'rgba(33, 150, 243, 0.08)' : 'inherit',
            border: skill.reference_patterns_details && skill.reference_patterns_details.length > 0
              ? '2px solid rgba(33, 150, 243, 0.6)'
              : 'none',
            borderRadius: skill.reference_patterns_details && skill.reference_patterns_details.length > 0
              ? 2
              : 0,
            mb: 0.5,
            '&:hover': { backgroundColor: skill.is_public ? 'rgba(33, 150, 243, 0.12)' : 'action.hover' }
          }}
          onClick={() => handleSkillClick(skill)}
        >
          {hasChildren && (
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); toggleSkill(skill.id); }}
              sx={{ mr: 0.5, p: 0.25 }}
            >
              {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          )}
          {!hasChildren && <BulletIcon sx={{ fontSize: 8, mr: 1 }} />}
          <ListItemText
            primary={skill.name}
            primaryTypographyProps={{ variant: 'body2' }}
            sx={{ my: 0 }}
          />
          <Tooltip title="Ajouter un sous-savoir-faire">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenCreateChildSkill({ id: skill.id, name: skill.name, container: skill.container }); }} sx={{ color: 'primary.main' }}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Ajouter une référence">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenRefPicker(skill.id); }} sx={{ color: 'secondary.main' }}>
              <AddLinkIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditSkill(skill); }}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); handleDeleteSkill(skill.id); }}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </ListItem>
        {hasChildren && isExpanded && (
          <Collapse in={isExpanded}>
            {renderChildSkills(skill.children_skills!, 1, skill.id, skill.container)}
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const toggleContainer = (containerId: number) => {
    setCollapsedContainers(prev => {
      const next = new Set(prev);
      if (next.has(containerId)) next.delete(containerId);
      else next.add(containerId);
      return next;
    });
  };

  // Filter containers/skills by search query
  const query = searchQuery.trim().toLowerCase();
  const filteredContainers = query
    ? rootContainers.map(root => {
        const matchedChildren = (root.children || []).map(sub => {
          const matchedSkills = (sub.skills || []).filter(s =>
            s.name.toLowerCase().includes(query) || s.description?.toLowerCase().includes(query)
          );
          return { ...sub, skills: matchedSkills };
        }).filter(sub => (sub.skills && sub.skills.length > 0) || sub.title.toLowerCase().includes(query));

        const matchedRootSkills = (root.skills || []).filter(s =>
          s.name.toLowerCase().includes(query) || s.description?.toLowerCase().includes(query)
        );

        const rootMatches = root.title.toLowerCase().includes(query);
        const hasContent = matchedChildren.length > 0 || matchedRootSkills.length > 0;

        if (!rootMatches && !hasContent) return null;
        return { ...root, children: matchedChildren, skills: matchedRootSkills };
      }).filter(Boolean) as Container[]
    : rootContainers;

  const renderSubcontainer = (subcontainer: Container) => (
    <Box key={subcontainer.id} sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1 }}>
          {subcontainer.title}
        </Typography>
        <IconButton size="small" onClick={() => handleEditContainer(subcontainer)}>
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => handleDeleteContainer(subcontainer.id)}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>

      <List dense>
        {subcontainer.skills && subcontainer.skills.filter(s => !s.parent).length > 0 ? (
          subcontainer.skills.filter(s => !s.parent).map((skill) => renderSkillItem(skill))
        ) : (
          <ListItem sx={{ py: 0.5, px: 0 }}>
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              Aucun savoir-faire
            </Typography>
          </ListItem>
        )}
      </List>

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={() => {
          setCreateType('skill');
          setSelectedParent(subcontainer.id);
          setCreateDialogOpen(true);
        }}
        sx={{ mt: 0.5 }}
      >
        Ajouter un savoir-faire
      </Button>
    </Box>
  );

  const renderRootContainer = (container: Container) => {
    const isCollapsed = collapsedContainers.has(container.id);
    const skillCount = (container.skills?.length || 0) +
      (container.children || []).reduce((sum, sub) => sum + (sub.skills?.length || 0), 0);

    return (
      <Paper
        key={container.id}
        sx={{
          p: 3,
          height: '100%',
          border: '2px solid',
          borderColor: 'divider',
          borderRadius: 3,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => toggleContainer(container.id)}
        >
          <IconButton size="small" sx={{ mr: 0.5 }}>
            {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
          <Typography variant="h6" sx={{ flex: 1 }}>
            {container.title}
            {isCollapsed && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                ({skillCount} savoir-faire)
              </Typography>
            )}
          </Typography>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditContainer(container); }}>
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); handleDeleteContainer(container.id); }}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon />
          </IconButton>
        </Box>

        <Collapse in={!isCollapsed}>
          <Divider sx={{ my: 2 }} />

          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {container.children && container.children.length > 0 ? (
              container.children.map(renderSubcontainer)
            ) : (
              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                Aucun sous-container
              </Typography>
            )}

            {/* Skills directly in root container */}
            {container.skills && container.skills.filter(s => !s.parent).length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Savoir-faire sans sous-container:
                </Typography>
                <List dense>
                  {container.skills.filter(s => !s.parent).map((skill) => renderSkillItem(skill))}
                </List>
              </Box>
            )}
          </Box>

          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 1 }}>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                setCreateType('subcontainer');
                setSelectedParent(container.id);
                setCreateDialogOpen(true);
              }}
              sx={{ flex: 1 }}
              variant="outlined"
            >
              Ajouter un sous-container
            </Button>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                setCreateType('skill');
                setSelectedParent(container.id);
                setCreateDialogOpen(true);
              }}
              sx={{ flex: 1 }}
              variant="outlined"
              color="secondary"
            >
              Ajouter un savoir-faire
            </Button>
          </Box>
        </Collapse>
      </Paper>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Savoir-faire</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setCreateType('root');
            setSelectedParent(null);
            setCreateDialogOpen(true);
          }}
        >
          Nouveau Container Principal
        </Button>
      </Box>

      <TextField
        size="small"
        placeholder="Rechercher un savoir-faire..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 2, maxWidth: 400 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
      />

      <Grid container spacing={3}>
        {filteredContainers.map((container) => (
          <Grid item xs={12} md={6} key={container.id}>
            {renderRootContainer(container)}
          </Grid>
        ))}
      </Grid>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {createType === 'root' && 'Nouveau Container Principal'}
          {createType === 'subcontainer' && 'Nouveau Sous-container'}
          {createType === 'skill' && 'Nouveau Savoir-faire'}
          {createType === 'child_skill' && `Nouveau sous-savoir-faire de "${parentSkillForChild?.name}"`}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nom"
            fullWidth
            variant="outlined"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            sx={{ mt: 2 }}
          />
          {(createType === 'skill' || createType === 'child_skill') && (
            <Box sx={{ mt: 2 }}>
              <Button
                startIcon={<TreeIcon />}
                onClick={() => handleOpenPatternTree(true)}
                variant="outlined"
                fullWidth
              >
                Sélectionner des patterns de référence
              </Button>

              {newPatterns.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Patterns sélectionnés ({newPatterns.length}):
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {newPatterns.map((pattern) => (
                      <Chip
                        key={pattern.id}
                        label={pattern.title}
                        size="small"
                        onDelete={() => setNewPatterns(newPatterns.filter(p => p.id !== pattern.id))}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreateDialogOpen(false);
            setNewTitle('');
            setSelectedParent(null);
            setParentSkillForChild(null);
            setNewPatterns([]);
          }}>
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (createType === 'root') handleCreateRootContainer();
              else if (createType === 'subcontainer') handleCreateSubcontainer();
              else if (createType === 'child_skill') handleCreateChildSkill();
              else handleCreateSkill();
            }}
          >
            Créer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Container Dialog */}
      <Dialog open={editContainerOpen} onClose={() => setEditContainerOpen(false)}>
        <DialogTitle>Modifier le Container</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nom"
            fullWidth
            variant="outlined"
            value={editContainerTitle}
            onChange={(e) => setEditContainerTitle(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditContainerOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleUpdateContainer}>Modifier</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Skill Dialog */}
      <Dialog open={editSkillOpen} onClose={() => setEditSkillOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: editSkillIsPublic ? 'rgba(33, 150, 243, 0.08)' : 'inherit' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Modifier le Savoir-faire</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={editSkillIsPublic}
                  onChange={(e) => setEditSkillIsPublic(e.target.checked)}
                  color="primary"
                />
              }
              label={editSkillIsPublic ? "Public" : "Privé"}
              sx={{ m: 0 }}
            />
          </Box>
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: editSkillIsPublic ? 'rgba(33, 150, 243, 0.08)' : 'inherit' }}>
          <TextField
            autoFocus
            margin="dense"
            label="Nom"
            fullWidth
            variant="outlined"
            value={editSkillName}
            onChange={(e) => setEditSkillName(e.target.value)}
            sx={{ mt: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={editSkillDescription}
            onChange={(e) => setEditSkillDescription(e.target.value)}
            sx={{ mt: 2 }}
          />

          <Box sx={{ mt: 2 }}>
            <Button
              startIcon={<TreeIcon />}
              onClick={() => handleOpenPatternTree(false)}
              variant="outlined"
              fullWidth
            >
              Sélectionner des patterns de référence
            </Button>

            {editSkillPatterns.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Patterns sélectionnés ({editSkillPatterns.length}):
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                  {editSkillPatterns.map((pattern) => (
                    <Chip
                      key={pattern.id}
                      label={pattern.title}
                      size="small"
                      onDelete={() => setEditSkillPatterns(editSkillPatterns.filter(p => p.id !== pattern.id))}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
          {editingSkill && <SkillCriteriaSection skillId={editingSkill.id} />}
        </DialogContent>
        <DialogActions sx={{ backgroundColor: editSkillIsPublic ? 'rgba(33, 150, 243, 0.08)' : 'inherit' }}>
          <Button onClick={() => setEditSkillOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleUpdateSkill}>Modifier</Button>
        </DialogActions>
      </Dialog>

      {/* Skill Detail Modal */}
      <Dialog open={skillDetailOpen} onClose={() => setSkillDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {detailSkill?.name}
        </DialogTitle>
        <DialogContent>
          {detailSkill?.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {detailSkill.description}
            </Typography>
          )}

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Patterns de référence directs
          </Typography>

          {detailSkill?.reference_patterns_details && detailSkill.reference_patterns_details.length > 0 ? (
            <List dense>
              {detailSkill.reference_patterns_details.map((pattern: any) => (
                <ListItem
                  key={pattern.id}
                  sx={{
                    cursor: 'pointer',
                    borderRadius: 1,
                    mb: 0.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': { backgroundColor: 'action.hover', borderColor: 'primary.main' },
                  }}
                  onClick={() => handlePatternNavigation(pattern)}
                >
                  <ListItemText
                    primary={pattern.title}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondary={pattern.container_title || null}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                  <Typography variant="caption" color="primary" sx={{ ml: 1, whiteSpace: 'nowrap' }}>
                    Voir →
                  </Typography>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              Aucun pattern de référence direct
            </Typography>
          )}

          {detailSkill?.children_skills && detailSkill.children_skills.length > 0 && (() => {
            const childPatterns = collectChildPatterns(detailSkill.children_skills);
            if (childPatterns.length === 0) return null;
            return (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Patterns hérités
                </Typography>
                {childPatterns.map((group, idx) => (
                  <Box key={idx} sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontStyle: 'italic' }}>
                      {group.skillName}
                    </Typography>
                    <List dense>
                      {group.patterns.map((pattern: any) => (
                        <ListItem
                          key={pattern.id}
                          sx={{
                            cursor: 'pointer',
                            borderRadius: 1,
                            mb: 0.5,
                            border: '1px solid',
                            borderColor: 'divider',
                            '&:hover': { backgroundColor: 'action.hover', borderColor: 'primary.main' },
                          }}
                          onClick={() => handlePatternNavigation(pattern)}
                        >
                          <ListItemText
                            primary={pattern.title}
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                            secondary={pattern.container_title || null}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                          <Typography variant="caption" color="primary" sx={{ ml: 1, whiteSpace: 'nowrap' }}>
                            Voir →
                          </Typography>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                ))}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSkillDetailOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Reference Picker Dialog */}
      <Dialog open={refPickerOpen} onClose={() => setRefPickerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <AddLinkIcon color="secondary" />
            <Typography variant="h6">Ajouter une référence</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Recherchez un savoir-faire existant pour l'ajouter comme référence.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Rechercher un savoir-faire..."
            value={refSearchQuery}
            onChange={(e) => handleRefSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ maxHeight: 300, overflow: 'auto', mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {refSearchLoading ? (
              <Box display="flex" justifyContent="center" p={2}><CircularProgress size={24} /></Box>
            ) : refSearchResults.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2, fontStyle: 'italic' }}>
                {refSearchQuery.length >= 2 ? 'Aucun résultat' : 'Tapez au moins 2 caractères'}
              </Typography>
            ) : (
              <List dense disablePadding>
                {refSearchResults.map((result) => (
                  <ListItem
                    key={result.id}
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                    onClick={() => handleAddReference(result.id)}
                  >
                    <ListItemText
                      primary={result.name}
                      secondary={result.container_title || 'Sans container'}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefPickerOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Pattern Tree Modal */}
      <Dialog
        open={patternTreeOpen}
        onClose={() => setPatternTreeOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: '90vw',
            height: '90vh',
            maxWidth: '90vw',
            maxHeight: '90vh',
            m: 0,
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" flexDirection="column" gap={2}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center" gap={1}>
                <TreeIcon />
                <Typography variant="h6">Arborescence des patterns</Typography>
              </Box>
              <Box display="flex" gap={1}>
                <Button
                  onClick={() => mindMapRef.current?.collapseAll()}
                  variant="outlined"
                  size="small"
                >
                  Fermer tous les dossiers
                </Button>
                <Button
                  onClick={() => {
                    mindMapRef.current?.enableExpandMode();
                    setIsExpandModeActive(true);
                    setIsCollapseSiblingsModeActive(false);
                  }}
                  variant={isExpandModeActive ? "contained" : "outlined"}
                  size="small"
                  color={isExpandModeActive ? "success" : "primary"}
                >
                  Ouvrir tous les noeuds fils
                </Button>
                <Button
                  onClick={() => {
                    mindMapRef.current?.enableCollapseSiblingsMode();
                    setIsCollapseSiblingsModeActive(true);
                    setIsExpandModeActive(false);
                  }}
                  variant={isCollapseSiblingsModeActive ? "contained" : "outlined"}
                  size="small"
                  color={isCollapseSiblingsModeActive ? "warning" : "primary"}
                >
                  Fermer les noeuds frères
                </Button>
                <Button onClick={() => setPatternTreeOpen(false)} variant="outlined">
                  Fermer
                </Button>
              </Box>
            </Box>
            <TextField
              size="small"
              placeholder="Rechercher un pattern ou container..."
              value={treeSearchQuery}
              onChange={(e) => setTreeSearchQuery(e.target.value)}
              fullWidth
              sx={{ maxWidth: '400px' }}
            />
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ height: 'calc(90vh - 160px)' }}>
          <PatternMindMap
            ref={mindMapRef}
            patterns={allPatternsForTree}
            containers={allContainersForTree}
            searchQuery={treeSearchQuery}
            onPatternClick={handlePatternTreeSelect}
            onContainerClick={() => {}} // Containers are not selectable for skills
            onExpandModeChange={setIsExpandModeActive}
            onCollapseSiblingsModeChange={setIsCollapseSiblingsModeActive}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Skills;
